import React, { useState } from 'react';
import { Modal, Button, Badge } from '@/components/ui';
import { PLAN_CONFIG } from '../types';
import type { Company, SubscriptionPlanCode } from '../types';

interface CompanyApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  company: Company | null;
  isLoading: boolean;
  onConfirm: (options: { planCode: SubscriptionPlanCode; endDate: string }) => void;
}

export function CompanyApprovalModal({ isOpen, onClose, company, isLoading, onConfirm }: CompanyApprovalModalProps) {
  const [planCode, setPlanCode] = useState<SubscriptionPlanCode>(company?.subscription_plan_code || 'free');

  // Default end date: 30 days from now
  const defaultEndDate = new Date();
  defaultEndDate.setDate(defaultEndDate.getDate() + 30);
  const [endDate, setEndDate] = useState(defaultEndDate.toISOString().split('T')[0]);

  if (!company) return null;

  const planCfg = PLAN_CONFIG[planCode];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Phê duyệt tổ chức"
      className="max-w-md"
    >
      <div className="p-6">
        <div className="flex items-center gap-4 mb-8 bg-slate-50 p-4 rounded-2xl border border-slate-100">
          <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
            <i className="ri-building-4-line text-2xl text-brand-600" />
          </div>
          <div>
            <h4 className="font-black text-slate-900 tracking-tight">{company.name}</h4>
            <p className="text-[10px] text-slate-500 font-mono  tracking-widest">{company.code}</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Plan Selection */}
          <div>
            <label className="block text-[10px] font-black text-slate-400  tracking-widest mb-3">Xác nhận Gói dịch vụ</label>
            <div className="grid grid-cols-2 gap-3">
              {(Object.keys(PLAN_CONFIG) as SubscriptionPlanCode[]).map((code) => {
                const cfg = PLAN_CONFIG[code];
                const isSelected = planCode === code;
                return (
                  <button
                    key={code}
                    onClick={() => setPlanCode(code)}
                    className={`p-3 rounded-xl border-2 transition-all text-left ${isSelected
                        ? `${cfg.border} bg-white shadow-md ring-2 ring-brand-500/10`
                        : 'border-slate-100 hover:border-slate-200 bg-slate-50/50'
                      }`}
                  >
                    <Badge variant="custom" className={`${cfg.badge} mb-1 h-5 text-[9px]`}>
                      {cfg.label}
                    </Badge>
                    <p className={`text-[11px] font-bold ${isSelected ? 'text-slate-900' : 'text-slate-500'}`}>
                      {code.charAt(0).toUpperCase() + code.slice(1)} Tier
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Expiration Date */}
          <div>
            <label className="block text-[10px] font-black text-slate-400  tracking-widest mb-2">Ngày hết hạn (Dự kiến)</label>
            <div className="relative">
              <i className="ri-calendar-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full h-12 pl-11 pr-4 bg-white border border-slate-100 rounded-xl focus:outline-none focus:ring-4 focus:ring-brand-500/10 shadow-sm text-sm font-bold text-slate-700"
              />
            </div>
            <p className="mt-2 text-[10px] text-slate-400 italic">Hệ thống sẽ tự động khóa Tenant sau ngày này nếu không gia hạn.</p>
          </div>
        </div>

        <div className="mt-10 flex gap-3">
          <Button variant="ghost" className="flex-1 h-12 rounded-xl" onClick={onClose}>
            Hủy bỏ
          </Button>
          <Button
            variant="primary"
            className="flex-2 h-12 rounded-xl px-8 bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-500/20"
            isLoading={isLoading}
            onClick={() => onConfirm({ planCode, endDate })}
          >
            Xác nhận Kích hoạt
          </Button>
        </div>
      </div>
    </Modal>
  );
}
